/**
 * Created by abilozor on 1/19/17.
 */

// Vendors
import { Injectable, EventEmitter } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Platform } from '@ionic/angular';
import { Subscription } from 'rxjs';
// NOTE: Do NOT statically import `@paymash/capacitor-scanner-app-plugin` here.
// The plugin's base class instantiates a Capacitor proxy at import time and
// registers a native listener in its constructor, which throws
// `CapacitorException: "ScannerAppPlugin" plugin is not implemented on android`
// because this plugin only exists on iOS. We load it lazily on iOS only.
import type { ScannerAppPlugin as ScannerAppPluginType } from '@paymash/capacitor-scanner-app-plugin';

@Injectable()
export class ScannerAppService {
  private scannerInited: boolean = false;
  private plugin: typeof ScannerAppPluginType | null = null;
  private connectSub?: Subscription;
  private barcodeSub?: Subscription;
  //public scannersListChangedEvent: EventEmitter<any> = new EventEmitter();
  public newBarcodeEvent: EventEmitter<string> = new EventEmitter();
  public activeScannerChangedEvent: EventEmitter<any> = new EventEmitter();
  public activeScanner: string = '';
  public scannersList: Array<string> = [];

  constructor(
    public TranslateService: TranslateService,
    private platform: Platform,
  ) {}

  private get isIos(): boolean {
    return this.platform.is('ios');
  }

  private async loadPlugin(): Promise<typeof ScannerAppPluginType | null> {
    if (!this.isIos) return null;
    if (this.plugin) return this.plugin;
    try {
      const mod = await import('@paymash/capacitor-scanner-app-plugin');
      this.plugin = mod.ScannerAppPlugin;
    } catch {
      this.plugin = null;
    }
    return this.plugin;
  }

  public async initAppScanner() {
    if (!this.isIos || this.scannerInited) return;
    const plugin = await this.loadPlugin();
    if (!plugin) return;

    this.scannerInited = true;
    //needed to send command in case of autoconnection
    this.connectToDevice('');
    plugin.init().subscribe(
      (scannersList) => {
        this.scannersList = scannersList.map((val) => val.displayName);
        // this.scannersListChangedEvent.emit();
      },
      () => {}
    );
  }

  public async connectToDevice(displayName: string) {
    if (!this.isIos) return;
    const plugin = await this.loadPlugin();
    if (!plugin) return;
    // Drop the previous connect subscription so repeated connectToDevice() calls
    // (init + every scanner pick in the select modal) don't accumulate listeners.
    this.connectSub?.unsubscribe();
    this.connectSub = plugin.connect(displayName).subscribe(
      (status) => {
        if (status.connected) {
          this.activeScanner = status.appInfo.displayName;
          //this.activeScannerChangedEvent.emit(appConnected);
          this.barcodeSub?.unsubscribe();
          this.barcodeSub = plugin.waitForBarcode().subscribe(
            (barcode) => {
              this.newBarcodeEvent.emit(barcode.code);
            },
            () => {}
          );
        } else {
          this.activeScanner = '';
          //this.activeScannerChangedEvent.emit('');
        }
      },
      () => {}
    );
  }

  public async disconnectDevice() {
    if (!this.isIos) return;
    const plugin = await this.loadPlugin();
    if (!plugin) return;
    this.barcodeSub?.unsubscribe();
    this.barcodeSub = undefined;
    plugin.disconnectDevice().then(
      () => {},
      () => {}
    );
  }

  public async disconnect() {
    if (!this.isIos || !this.scannerInited) return;
    const plugin = await this.loadPlugin();
    if (!plugin) return;
    this.scannerInited = false;
    this.connectSub?.unsubscribe();
    this.connectSub = undefined;
    this.barcodeSub?.unsubscribe();
    this.barcodeSub = undefined;
    plugin.disconnectSession().then(
      () => {},
      () => {}
    );
  }
}
